import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | submitting | sent | error
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setPhase("submitting");
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setPhase("sent");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong.");
      setPhase("error");
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-6 py-14" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm mb-4"
              style={{ color: "var(--text-secondary)" }}
              data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="card-surface p-6 sm:p-8">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
               style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
            <Mail className="w-5 h-5" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
            Reset your password
          </h1>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            Enter the email you registered with. If we find an account, we'll send you a reset link. The link expires in 1 hour.
          </p>

          {phase === "sent" ? (
            <div
              className="flex items-start gap-3 p-4 rounded-lg"
              style={{ background: "#EFF9F1", border: "1px solid #BEE3C7" }}
              data-testid="forgot-sent-state"
            >
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#0F5D2E" }} />
              <div className="text-sm" style={{ color: "#0F5D2E" }}>
                <div className="font-semibold mb-1">Check your inbox</div>
                <div>If an account exists for <strong>{email}</strong>, a reset link is on its way. Give it a couple of minutes and check your spam folder if you don't see it.</div>
                <button
                  type="button"
                  className="btn-primary text-sm mt-3"
                  onClick={() => nav("/login")}
                  data-testid="forgot-back-to-login-btn"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="field-label">Email</label>
                <input
                  type="email"
                  required
                  className="field-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                  autoComplete="email"
                />
              </div>
              {err && (
                <div className="text-sm px-3 py-2 rounded" style={{ background: "#FDECE7", color: "#B54234" }}>
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={phase === "submitting" || !email.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="forgot-submit-btn"
              >
                {phase === "submitting" && <Loader2 className="w-4 h-4 animate-spin" />}
                Send reset link
              </button>
            </form>
          )}
        </div>
      </div>
    </AppShell>
  );
}
